import React, { Fragment, Suspense, useMemo } from "react";

import { css } from "@compiled/react";

import type { ExtensionHandlers } from "@atlaskit/editor-common/extensions";
import { type RendererProps } from "@atlaskit/renderer";
import { token } from "@atlaskit/tokens";

import { Button, Link } from "@/libraries/design/components";

import { Source } from "../ai-summary";

import { encodeAdf } from "./encoder";

const AkReactRenderer = React.lazy(() =>
    import("@atlaskit/renderer").then(({ ReactRenderer }) => ({
        default: ReactRenderer
    }))
);

export type MarkdownRendererProps = {
    answerString: string;
    statusComponent: (props: {
        text?: string;
        sources?: Source[];
    }) => React.ReactNode;
};

const MediaComponent = () => <Fragment />;
const MediaSingleComponent = () => <Fragment />;

const buttonStyle = css({
    display: "inline-block",
    marginInlineEnd: token("space.100")
});

const ButtonComponent = ({
    text,
    buttonType,
    href
}: {
    text: string;
    buttonType: string;
    href: string;
}) => {
    if (buttonType === "navigation") {
        return (
            <Link
                href={href}
                type="primary"
                aria-label={`${text}button`}
                text={text}
            />
        );
    }
    return (
        <Button variant="secondary" colorAgainst="white">
            {text}
        </Button>
    );
};

// Extension handler for CTA buttons
// Handles inlineExtension nodes with extensionKey: "cta-button"
const ctaButtonExtensionHandler: ExtensionHandlers = {
    "com.atlassian.wac": (ext) => {
        const { extensionKey, parameters } = ext;

        if (extensionKey === "cta-button" && parameters) {
            const { text, buttonType, href } = parameters as {
                text: string;
                buttonType: string;
                href: string;
            };

            // Wrap in span to ensure inline rendering
            return (
                <div css={buttonStyle}>
                    <ButtonComponent
                        text={text}
                        buttonType={buttonType}
                        href={href}
                    />
                </div>
            );
        }

        return null;
    }
};

export const MarkdownRenderer = ({
    answerString,
    statusComponent
}: MarkdownRendererProps) => {
    const docContent = useMemo(
        () => encodeAdf(answerString, false),
        [answerString]
    );

    const config: RendererProps = {
        // @ts-expect-error - DocNode type unavailable, fallback to unknown
        document: docContent as unknown,
        nodeComponents: {
            status: statusComponent,
            media: MediaComponent,
            mediaSingle: MediaSingleComponent
        },
        extensionHandlers: ctaButtonExtensionHandler,
        smartLinks: {
            ssr: true
        }
    };

    return (
        <Suspense fallback={<Fragment />}>
            <AkReactRenderer {...config} />
        </Suspense>
    );
};
